// Renders the control sections (Shape, Color, Audio) for the MenuSheet.
// The Source section is built separately by SourcePicker.buildSourceSection().
//
// SECURITY: never use innerHTML — DOM is built via createElement +
// textContent only. The hookify rule blocks innerHTML at edit time.

import { Slider } from './slider.js';
import { setQualityPreset, getQualityPreset, QUALITY_PRESET_NAMES } from '../engine/renderer.js';
import { PALETTES } from '../scene/palettes.js';
import {
  HARMONIES, hexToRgb01, rgb01ToHex, rgbToHsl, hslToRgb, kmeans3,
} from './palette-generators.js';

export {
  HARMONIES, hexToRgb01, rgb01ToHex, rgbToHsl, hslToRgb, kmeans3,
  harmonyAnalogous, harmonyComplementary, harmonyTriadic,
  harmonySplitComp, harmonyMonochromatic, harmonyChaos,
} from './palette-generators.js';

const STYLE_ID = 'dp-controls-style';

const STYLE = `
.dp-controls-shape, .dp-controls-color, .dp-controls-audio {
  display: flex; flex-direction: column; gap: 14px;
}

/* Soundboard pads — kept visually identical to the legacy strip */
.dp-soundboard {
  display: flex; flex-direction: column; gap: 10px;
}
.dp-sb-group { display: flex; flex-direction: column; gap: 4px; }
.dp-sb-label {
  font: 9px/1 ui-monospace, monospace; letter-spacing: 0.1em;
  text-transform: uppercase; color: rgba(255,255,255,0.5);
}
.dp-sb-pads { display: flex; gap: 6px; }
.dp-sb-pad {
  flex: 1; min-width: 0; min-height: 38px; padding: 8px 4px;
  background: linear-gradient(180deg, rgba(40,40,46,0.95), rgba(14,14,18,0.95));
  border: 1px solid rgba(255,255,255,0.1); border-radius: 5px;
  color: rgba(255,255,255,0.78); cursor: pointer;
  font: 10px/1.1 ui-monospace, monospace; letter-spacing: 0.04em;
  text-transform: uppercase;
  box-shadow: inset 0 -2px 3px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.05);
  text-align: center; transition: transform 60ms, filter 120ms;
}
.dp-sb-pad:active { transform: translateY(1px); }
.dp-sb-leds { display: flex; gap: 6px; padding: 0 2px; }
.dp-sb-led {
  flex: 1; height: 4px; border-radius: 2px;
  background: rgba(255,255,255,0.06);
  transition: background 100ms, box-shadow 100ms;
}
.dp-sb-led.lit {
  background: #3f7;
  box-shadow: 0 0 5px #3f7, 0 0 1px #fff;
}
.dp-sb-reset {
  padding: 8px 10px;
  background: rgba(255,80,80,0.12);
  border: 1px solid rgba(255,80,80,0.35);
  border-radius: 5px; color: #fcc; cursor: pointer;
  font: 11px/1 ui-monospace, monospace; letter-spacing: 0.08em;
  text-transform: uppercase;
}
.dp-sb-reset:hover { background: rgba(255,80,80,0.22); }

/* Slider stack — one slider per row, label | track | value */
.dp-slider-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 6px;
}

/* DJ-style horizontal slider visuals */
.dj-slider {
  display: grid;
  grid-template-columns: 64px 1fr 40px;
  align-items: center;
  gap: 10px;
  outline: none;
  min-height: 28px;
}
.dj-slider:focus .dj-slider-track {
  box-shadow: 0 0 0 2px rgba(100,200,255,0.4);
}
.dj-slider-label {
  font: 9px/1 ui-monospace, monospace; letter-spacing: 0.08em;
  text-transform: uppercase; color: rgba(255,255,255,0.65);
}
.dj-slider-value {
  font: 10px/1 ui-monospace, monospace;
  color: rgba(255,255,255,0.85);
  text-align: right;
  min-height: 12px;
}
.dj-slider-track {
  position: relative; height: 20px; width: 100%;
  background: linear-gradient(180deg, rgba(0,0,0,0.55), rgba(20,22,28,0.55));
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 6px;
  touch-action: none; cursor: ew-resize;
}
.dj-slider-centerline {
  position: absolute; top: 50%; left: 4px; right: 4px;
  height: 1.5px; margin-top: -0.75px;
  background: rgba(255,255,255,0.55);
  border-radius: 1px;
}
.dj-slider-leds {
  position: absolute; left: 4px; right: 4px; bottom: 3px;
  height: 4px;
  display: flex; justify-content: space-between;
  pointer-events: none;
}
.dj-slider-led {
  width: 4px; height: 4px; border-radius: 50%;
  background: rgba(255,255,255,0.08);
  transition: background 100ms, box-shadow 100ms;
}
.dj-slider-led.lit {
  background: #3f7;
  box-shadow: 0 0 4px #3f7;
}
.dj-slider-cap {
  position: absolute; top: 1px;
  width: 14px; height: 16px;
  background: linear-gradient(180deg, #c8d0db, #6c7686);
  border: 1px solid rgba(0,0,0,0.4);
  border-radius: 3px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.55);
  pointer-events: none;
}

/* Color section: gradient bar + tabs (the popup is now inline) */
.dp-palette-row { display: flex; align-items: center; gap: 10px; }
.dp-palette-name {
  font: 9px/1 ui-monospace, monospace; letter-spacing: 0.08em;
  text-transform: uppercase; color: rgba(255,255,255,0.55); min-width: 64px;
}
.dp-palette-bar {
  flex: 1; height: 20px; border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.18);
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.4);
}
.dp-palette-tabs {
  display: flex; gap: 4px; margin-top: 4px; flex-wrap: wrap;
}
.dp-palette-tab {
  padding: 6px 10px; font: 10px/1 ui-monospace, monospace;
  letter-spacing: 0.05em; text-transform: uppercase;
  color: rgba(255,255,255,0.55); background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1); border-radius: 4px;
  cursor: pointer;
}
.dp-palette-tab.active { background: rgba(100,200,255,0.18); color: #fff; border-color: rgba(100,200,255,0.4); }
.dp-palette-tab-panel { display: none; padding-top: 6px; }
.dp-palette-tab-panel.active { display: block; }

.dp-preset-list { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.dp-preset {
  display: flex; align-items: center; gap: 8px; padding: 6px 8px;
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 5px; cursor: pointer;
}
.dp-preset:hover { background: rgba(255,255,255,0.08); }
.dp-preset.active { border-color: rgba(100,200,255,0.5); background: rgba(100,200,255,0.08); }
.dp-preset-swatch {
  width: 36px; height: 14px; border-radius: 3px;
  border: 1px solid rgba(255,255,255,0.15);
}
.dp-preset-name {
  font: 10px/1 ui-monospace, monospace; letter-spacing: 0.04em;
  text-transform: uppercase; color: rgba(255,255,255,0.7);
}

.dp-gen-base-row {
  display: flex; align-items: center; gap: 8px; margin-bottom: 6px;
  font: 10px/1 ui-monospace, monospace; color: rgba(255,255,255,0.65);
}
.dp-gen-base-row input[type="color"] {
  width: 36px; height: 26px; padding: 0; border: 1px solid rgba(255,255,255,0.2);
  border-radius: 3px; background: transparent; cursor: pointer;
}
.dp-gen-row { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.dp-gen-btn {
  padding: 8px; background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.1); border-radius: 4px;
  color: rgba(255,255,255,0.78); cursor: pointer;
  font: 10px/1 ui-monospace, monospace; letter-spacing: 0.04em;
  text-transform: uppercase;
}
.dp-gen-btn:hover { background: rgba(255,255,255,0.08); }

.dp-manual-stop {
  display: grid; grid-template-columns: 30px 1fr; gap: 8px;
  align-items: center; margin-bottom: 8px;
  padding: 8px; background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.06); border-radius: 5px;
}
.dp-manual-swatch {
  width: 30px; height: 60px; border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.15);
}
.dp-manual-sliders {
  display: grid; grid-template-columns: 16px 1fr 36px; gap: 4px 6px;
  align-items: center; font: 9px/1 ui-monospace, monospace;
  color: rgba(255,255,255,0.55);
}
.dp-manual-sliders input[type="range"] { width: 100%; margin: 0; }
.dp-manual-actions { display: flex; gap: 6px; margin-top: 6px; }
.dp-manual-actions .dp-gen-btn { flex: 1; }

.dp-image-row {
  display: flex; flex-direction: column; gap: 6px;
  font: 10px/1.4 ui-monospace, monospace; color: rgba(255,255,255,0.7);
}
.dp-image-status {
  font-size: 9px; color: rgba(255,255,255,0.45); min-height: 11px;
}

.dp-controls-resolution {
  display: flex; flex-direction: column; gap: 8px;
}
.dp-res-hint {
  font: 9.5px/1.4 ui-monospace, monospace;
  color: rgba(255,255,255,0.55);
  padding: 4px 2px 2px;
}
`;

function rgbToHex(r, g, b) {
  const c = (v) => Math.round(Math.max(0, Math.min(1, v)) * 255)
    .toString(16).padStart(2, '0');
  return '#' + c(r) + c(g) + c(b);
}

function paletteGradient(stops) {
  const [a, b, c] = stops;
  return `linear-gradient(90deg, ${rgbToHex(a[0], a[1], a[2])} 0%, `
    + `${rgbToHex(b[0], b[1], b[2])} 50%, `
    + `${rgbToHex(c[0], c[1], c[2])} 100%)`;
}

// crossfade(t, lo, hi) — given t in [0,1], emits two values that crossfade.
// Both reach their midpoint at t=0.5. Used by Shape Energy + Color Reactivity
// merged sliders so one slider drives two shader uniforms with reasonable
// curve coverage of each.
function crossfade(t, loMax, hiMax) {
  return { lo: t * loMax, hi: t * hiMax };
}

export class KnobPanel {
  constructor({ fractal, renderer }) {
    this.fractal = fractal;
    this.renderer = renderer || null;
    this.sliders = [];
    this._activeTab = 'library';
    this._onKey = this._onKey.bind(this);
  }

  // Build the three control sections. Returns { shape, color, audio } —
  // each is a DOM node ready to be wrapped in a collapsibleSection().
  buildSections() {
    this._injectStyle();
    const f = this.fractal;

    // ---------- SHAPE ----------
    const shape = document.createElement('div');
    shape.className = 'dp-controls-shape';
    shape.appendChild(this._buildSoundboard());

    const shapeSliders = this._sliderGrid([
      // Merged: Intensity (0-2) + Breathe (0-2). One slider 0..1 → both proportionally.
      {
        label: 'Energy', default: 0.5, min: 0, max: 1,
        format: (v) => v.toFixed(2),
        apply: (v) => {
          const { lo, hi } = crossfade(v, 2.0, 2.0);
          try { f.setIntensity?.(lo); } catch {}
          try { f.setBreatheAmp?.(hi); } catch {}
        },
      },
      { label: 'Trail',  default: 0.92, min: 0.8, max: 0.99, step: 0.005, apply: (v) => f.setTrail?.(v) },
      { label: 'Spike',  default: 1.0,  min: 0,   max: 2, apply: (v) => f.setSpikeMult?.(v) },
      { label: 'Kaleido', default: 6,   min: 2,   max: 12, step: 1,
        format: (v) => Math.round(v).toString(),
        apply: (v) => f.setKaleidoFolds?.(v) },
    ]);
    shape.appendChild(shapeSliders);

    // ---------- COLOR ----------
    const color = document.createElement('div');
    color.className = 'dp-controls-color';

    const palRow = document.createElement('div');
    palRow.className = 'dp-palette-row';
    const palName = document.createElement('div');
    palName.className = 'dp-palette-name';
    palRow.appendChild(palName);
    const palBar = document.createElement('div');
    palBar.className = 'dp-palette-bar';
    palRow.appendChild(palBar);
    color.appendChild(palRow);

    // Seed _paletteRefs BEFORE _buildPaletteTabs runs — that method spreads
    // the existing refs and adds tabEls/presetEls/wrap. Doing it after
    // would clobber tabEls and silently break tab clicks (Image / Generate
    // / Manual would all show empty Library panel).
    this._paletteRefs = { palName, palBar };
    color.appendChild(this._buildPaletteTabs());

    const colorSliders = this._sliderGrid([
      // Merged: Chroma (0-2) + Palette strength (0-1).
      {
        label: 'Color Rx', default: 0.5, min: 0, max: 1,
        format: (v) => v.toFixed(2),
        apply: (v) => {
          const { lo, hi } = crossfade(v, 2.0, 1.0);
          try { f.setChromaCap?.(lo); } catch {}
          try { f.setPaletteStrength?.(hi); } catch {}
        },
      },
      { label: 'FreqBal', default: 0.5,  min: 0,  max: 1, apply: (v) => f.setFreqBalance?.(v) },
    ]);
    color.appendChild(colorSliders);

    // ---------- AUDIO ----------
    const audio = document.createElement('div');
    audio.className = 'dp-controls-audio';
    const audioSliders = this._sliderGrid([
      { label: 'Bass',  default: 1.0, min: 0, max: 2, apply: (v) => f.setBassImpact?.(v) },
      { label: 'Mids',  default: 1.0, min: 0, max: 2, apply: (v) => f.setMidsImpact?.(v) },
      { label: 'Highs', default: 1.0, min: 0, max: 2, apply: (v) => f.setTrebleImpact?.(v) },
    ]);
    audio.appendChild(audioSliders);

    // Resolution is its own top-level section (returned alongside shape/color/audio).
    const resolution = this._buildResolutionSection();

    // Listen for P key to refresh the palette display when mode-switcher cycles
    window.addEventListener('keydown', this._onKey);

    // Push starting values into the shader so the visible slider state matches.
    queueMicrotask(() => {
      for (const s of this.sliders) {
        try { s.spec.apply(s.spec.default); } catch {}
      }
      this._refreshPalette();
      const initStops = this._readManualStops();
      if (initStops) this._updateManualSwatches(initStops);
      this._refreshSoundboard();
    });

    return { shape, color, audio, resolution };
  }

  destroy() {
    window.removeEventListener('keydown', this._onKey);
    for (const s of this.sliders) {
      try { s.slider.destroy?.(); } catch {}
    }
    this.sliders = [];
  }

  // ----- Internals -----

  _injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLE;
    document.head.appendChild(style);
  }

  _sliderGrid(specs) {
    const grid = document.createElement('div');
    grid.className = 'dp-slider-grid';
    for (const spec of specs) {
      const slider = new Slider({
        label: spec.label,
        value: spec.default,
        default: spec.default,
        min: spec.min,
        max: spec.max,
        step: spec.step ?? 0.01,
        format: spec.format,
      });
      slider.mount(grid);
      slider.addEventListener('change', (e) => {
        try { spec.apply(e.value); } catch {}
      });
      this.sliders.push({ slider, spec });
    }
    return grid;
  }

  // Resolution sub-section: one stepped slider that picks a quality preset
  // (auto / low / medium / high / ultra) plus a hint line explaining what
  // each level is for. Slider value is the index into QUALITY_PRESET_NAMES.
  _buildResolutionSection() {
    const body = document.createElement('div');
    body.className = 'dp-controls-resolution';

    const initialName = getQualityPreset();
    const initialIdx = Math.max(0, QUALITY_PRESET_NAMES.indexOf(initialName));

    const grid = document.createElement('div');
    grid.className = 'dp-slider-grid';
    const slider = new Slider({
      label: 'Quality',
      value: initialIdx,
      default: 0,
      min: 0,
      max: QUALITY_PRESET_NAMES.length - 1,
      step: 1,
      format: (v) => {
        const name = QUALITY_PRESET_NAMES[Math.round(v)] || 'auto';
        return name.charAt(0).toUpperCase() + name.slice(1);
      },
    });
    slider.mount(grid);
    body.appendChild(grid);

    const hint = document.createElement('div');
    hint.className = 'dp-res-hint';
    body.appendChild(hint);

    const HINTS = {
      auto:   'Picks a budget based on your device. Recommended.',
      low:    'For phones, on-battery laptops, or thermal throttling.',
      medium: 'For most laptops (MBA / MBP).',
      high:   'For iMacs and desktops with discrete GPUs.',
      ultra:  'Cathedral-original baseline. Needs a workstation GPU.',
    };
    const updateHint = (name) => { hint.textContent = HINTS[name] || ''; };
    updateHint(initialName);

    slider.addEventListener('change', (e) => {
      const name = QUALITY_PRESET_NAMES[Math.round(e.value)] || 'auto';
      try { setQualityPreset(name); } catch {}
      updateHint(name);
      // Trigger a real resize so the new caps take effect immediately.
      try { this.renderer?.resize?.(); } catch {}
    });
    // Stash a ref so destroy() can clean it up alongside the audio sliders.
    this.sliders.push({ slider, spec: { default: initialIdx } });

    return body;
  }

  _buildSoundboard() {
    const sb = document.createElement('div');
    sb.className = 'dp-soundboard';
    const f = this.fractal;

    this._sbLeds = { fractal: [], view: [], cathedral: [] };
    const groups = [
      {
        key: 'fractal', label: 'Fractal',
        options: ['Bulb', 'Sacred', 'Lava', 'Point'],
        currentIndex: () => Number(f?.currentFractalId ?? 0),
        onSelect: (i) => f?.setFractal?.(i),
      },
      {
        key: 'view', label: 'View',
        options: ['Fly', 'Orbit', 'Stable'],
        currentIndex: () => {
          const map = ['fly', 'orbit', 'stabilized'];
          return Math.max(0, map.indexOf(f?.cameraMode ?? 'stabilized'));
        },
        onSelect: (i) => {
          const map = ['fly', 'orbit', 'stabilized'];
          f?.setCameraMode?.(map[i]);
        },
      },
      {
        key: 'cathedral', label: 'Cathedral',
        options: ['Off', 'On'],
        currentIndex: () => (f?.cathedralOn ? 1 : 0),
        onSelect: (i) => f?.setCathedral?.(i === 1),
      },
    ];

    for (const g of groups) {
      const group = document.createElement('div');
      group.className = 'dp-sb-group';
      const lbl = document.createElement('div');
      lbl.className = 'dp-sb-label';
      lbl.textContent = g.label;
      group.appendChild(lbl);
      const pads = document.createElement('div');
      pads.className = 'dp-sb-pads';
      const leds = document.createElement('div');
      leds.className = 'dp-sb-leds';
      for (let i = 0; i < g.options.length; i++) {
        const pad = document.createElement('button');
        pad.type = 'button';
        pad.className = 'dp-sb-pad';
        pad.textContent = g.options[i];
        pad.addEventListener('click', (e) => {
          e.stopPropagation();
          try { g.onSelect(i); } catch {}
          this._refreshSoundboard();
        });
        pads.appendChild(pad);
        const led = document.createElement('div');
        led.className = 'dp-sb-led';
        leds.appendChild(led);
        this._sbLeds[g.key].push(led);
      }
      group.appendChild(pads);
      group.appendChild(leds);
      sb.appendChild(group);
    }
    this._sbGroups = groups;

    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'dp-sb-reset';
    reset.textContent = 'Reset to stable';
    reset.addEventListener('click', (e) => {
      e.stopPropagation();
      this._resetToStable();
    });
    sb.appendChild(reset);

    return sb;
  }

  _refreshSoundboard() {
    if (!this._sbGroups || !this._sbLeds) return;
    for (const g of this._sbGroups) {
      const active = g.currentIndex();
      const leds = this._sbLeds[g.key];
      for (let i = 0; i < leds.length; i++) {
        leds[i].classList.toggle('lit', i === active);
      }
    }
  }

  _resetToStable() {
    try { this.fractal?.setCameraMode?.('stabilized'); } catch {}
    for (const s of this.sliders) {
      try {
        s.slider.setValue(s.spec.default);
        s.spec.apply?.(s.spec.default);
      } catch {}
    }
    this._refreshSoundboard();
  }

  // ----- Palette UI (tabs, presets, generate, manual, image) -----

  _buildPaletteTabs() {
    const wrap = document.createElement('div');

    const tabs = document.createElement('div');
    tabs.className = 'dp-palette-tabs';
    const tabsSpec = [
      { id: 'library', label: 'Library' },
      { id: 'image', label: 'Image' },
      { id: 'generate', label: 'Generate' },
      { id: 'manual', label: 'Manual' },
    ];
    const tabEls = {};
    for (const t of tabsSpec) {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'dp-palette-tab';
      if (t.id === this._activeTab) tab.classList.add('active');
      tab.textContent = t.label;
      tab.addEventListener('click', () => this._setActiveTab(t.id));
      tabs.appendChild(tab);
      tabEls[t.id] = tab;
    }
    wrap.appendChild(tabs);

    // Library
    const lib = document.createElement('div');
    lib.className = 'dp-palette-tab-panel active';
    lib.dataset.panel = 'library';
    const list = document.createElement('div');
    list.className = 'dp-preset-list';
    const presetEls = [];
    for (let i = 0; i < PALETTES.length; i++) {
      const p = PALETTES[i];
      const preset = document.createElement('button');
      preset.type = 'button';
      preset.className = 'dp-preset';
      const swatch = document.createElement('div');
      swatch.className = 'dp-preset-swatch';
      swatch.style.background = paletteGradient(p.stops);
      preset.appendChild(swatch);
      const name = document.createElement('div');
      name.className = 'dp-preset-name';
      name.textContent = p.name;
      preset.appendChild(name);
      preset.addEventListener('click', () => this._selectPalette(i));
      list.appendChild(preset);
      presetEls.push(preset);
    }
    lib.appendChild(list);
    wrap.appendChild(lib);

    // Image
    const img = document.createElement('div');
    img.className = 'dp-palette-tab-panel';
    img.dataset.panel = 'image';
    const imgRow = document.createElement('div');
    imgRow.className = 'dp-image-row';
    const imgLbl = document.createElement('div');
    imgLbl.textContent = 'Pick an image to extract a 3-color palette (k-means).';
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    const imgStatus = document.createElement('div');
    imgStatus.className = 'dp-image-status';
    fileInput.addEventListener('change', (e) => {
      const f = e.target.files && e.target.files[0];
      if (!f) return;
      this._loadImagePalette(f, imgStatus);
    });
    imgRow.appendChild(imgLbl);
    imgRow.appendChild(fileInput);
    imgRow.appendChild(imgStatus);
    img.appendChild(imgRow);
    wrap.appendChild(img);

    // Generate
    const gen = document.createElement('div');
    gen.className = 'dp-palette-tab-panel';
    gen.dataset.panel = 'generate';
    const baseRow = document.createElement('div');
    baseRow.className = 'dp-gen-base-row';
    const baseLbl = document.createElement('label');
    baseLbl.textContent = 'BASE';
    const baseColor = document.createElement('input');
    baseColor.type = 'color';
    baseColor.value = '#ff6a00';
    baseRow.appendChild(baseLbl);
    baseRow.appendChild(baseColor);
    gen.appendChild(baseRow);
    const genRow = document.createElement('div');
    genRow.className = 'dp-gen-row';
    const genButtons = [
      { label: 'Analogous', key: 'analogous' },
      { label: 'Complementary', key: 'complementary' },
      { label: 'Triadic', key: 'triadic' },
      { label: 'Split-Comp', key: 'splitComp' },
      { label: 'Monochromatic', key: 'monochromatic' },
      { label: 'Harmonic Chaos', key: 'chaos' },
    ];
    for (const b of genButtons) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dp-gen-btn';
      btn.textContent = b.label;
      btn.addEventListener('click', () => {
        const fn = HARMONIES[b.key];
        if (!fn) return;
        const stops = fn(baseColor.value);
        this._setCustomPaletteAndRefresh(stops);
        this._syncManualFromStops(stops);
      });
      genRow.appendChild(btn);
    }
    gen.appendChild(genRow);
    wrap.appendChild(gen);

    // Manual
    const manual = document.createElement('div');
    manual.className = 'dp-palette-tab-panel';
    manual.dataset.panel = 'manual';
    const manualStops = [
      [0.95, 0.4, 0.2],
      [0.2, 0.5, 0.95],
      [0.6, 0.3, 0.8],
    ];
    const manualRefs = [];
    for (let i = 0; i < 3; i++) {
      const stopRow = document.createElement('div');
      stopRow.className = 'dp-manual-stop';
      const swatch = document.createElement('div');
      swatch.className = 'dp-manual-swatch';
      stopRow.appendChild(swatch);
      const sliders = document.createElement('div');
      sliders.className = 'dp-manual-sliders';
      const sliderRefs = {};
      const [h0, s0, l0] = rgbToHsl(...manualStops[i]);
      const initial = { h: h0, s: s0, l: l0 };
      for (const ch of ['h', 's', 'l']) {
        const lab = document.createElement('div');
        lab.textContent = ch.toUpperCase();
        const input = document.createElement('input');
        input.type = 'range';
        input.min = '0';
        input.max = '1';
        input.step = '0.001';
        input.value = String(initial[ch]);
        const readout = document.createElement('div');
        readout.textContent = initial[ch].toFixed(2);
        input.addEventListener('input', () => {
          const v = Number(input.value);
          readout.textContent = v.toFixed(2);
          this._onManualSliderChange();
        });
        sliders.appendChild(lab);
        sliders.appendChild(input);
        sliders.appendChild(readout);
        sliderRefs[ch] = { input, readout };
      }
      stopRow.appendChild(sliders);
      manual.appendChild(stopRow);
      manualRefs.push({ swatch, sliders: sliderRefs });
    }
    const manualActions = document.createElement('div');
    manualActions.className = 'dp-manual-actions';
    const exportBtn = document.createElement('button');
    exportBtn.type = 'button';
    exportBtn.className = 'dp-gen-btn';
    exportBtn.textContent = 'Export hex';
    exportBtn.addEventListener('click', () => this._exportManualHex());
    const importBtn = document.createElement('button');
    importBtn.type = 'button';
    importBtn.className = 'dp-gen-btn';
    importBtn.textContent = 'Import hex';
    importBtn.addEventListener('click', () => this._importManualHex());
    manualActions.appendChild(exportBtn);
    manualActions.appendChild(importBtn);
    manual.appendChild(manualActions);
    wrap.appendChild(manual);

    this._manualRefs = manualRefs;
    this._paletteRefs = { ...(this._paletteRefs || {}), presetEls, tabEls, wrap };
    return wrap;
  }

  _setActiveTab(id) {
    this._activeTab = id;
    if (!this._paletteRefs?.tabEls) return;
    for (const [k, el] of Object.entries(this._paletteRefs.tabEls)) {
      el.classList.toggle('active', k === id);
    }
    const panels = this._paletteRefs.wrap.querySelectorAll('.dp-palette-tab-panel');
    for (const p of panels) p.classList.toggle('active', p.dataset.panel === id);
  }

  _selectPalette(id) {
    this.fractal?.setPalette?.(id);
    this._refreshPalette();
  }

  _setCustomPaletteAndRefresh(stops) {
    this.fractal?.setCustomPalette?.(stops);
    this._refreshPaletteGradient(stops);
    if (this._paletteRefs?.presetEls) {
      for (const el of this._paletteRefs.presetEls) el.classList.remove('active');
    }
    if (this._paletteRefs?.palName) this._paletteRefs.palName.textContent = 'custom';
  }

  _refreshPaletteGradient(stops) {
    if (this._paletteRefs?.palBar) this._paletteRefs.palBar.style.background = paletteGradient(stops);
  }

  _refreshPalette() {
    if (!this._paletteRefs) return;
    const id = this.fractal?._paletteId ?? 0;
    const p = PALETTES[id] || PALETTES[0];
    if (this._paletteRefs.palName) this._paletteRefs.palName.textContent = p.name;
    if (this._paletteRefs.palBar) this._paletteRefs.palBar.style.background = paletteGradient(p.stops);
    if (this._paletteRefs.presetEls) {
      for (let i = 0; i < this._paletteRefs.presetEls.length; i++) {
        this._paletteRefs.presetEls[i].classList.toggle('active', i === id);
      }
    }
  }

  _readManualStops() {
    if (!this._manualRefs) return null;
    const stops = [];
    for (const r of this._manualRefs) {
      const h = Number(r.sliders.h.input.value);
      const s = Number(r.sliders.s.input.value);
      const l = Number(r.sliders.l.input.value);
      stops.push(hslToRgb(h, s, l));
    }
    return stops;
  }

  _updateManualSwatches(stops) {
    if (!this._manualRefs) return;
    for (let i = 0; i < this._manualRefs.length; i++) {
      const [r, g, b] = stops[i] || [0, 0, 0];
      this._manualRefs[i].swatch.style.background = rgb01ToHex([r, g, b]);
    }
  }

  _syncManualFromStops(stops) {
    if (!this._manualRefs) return;
    for (let i = 0; i < this._manualRefs.length; i++) {
      const [r, g, b] = stops[i] || [0, 0, 0];
      const [h, s, l] = rgbToHsl(r, g, b);
      const refs = this._manualRefs[i].sliders;
      refs.h.input.value = String(h);
      refs.h.readout.textContent = h.toFixed(2);
      refs.s.input.value = String(s);
      refs.s.readout.textContent = s.toFixed(2);
      refs.l.input.value = String(l);
      refs.l.readout.textContent = l.toFixed(2);
    }
    this._updateManualSwatches(stops);
  }

  _onManualSliderChange() {
    const stops = this._readManualStops();
    if (!stops) return;
    this._updateManualSwatches(stops);
    this._setCustomPaletteAndRefresh(stops);
  }

  _exportManualHex() {
    const stops = this._readManualStops();
    if (!stops) return;
    const text = stops.map((s) => rgb01ToHex(s)).join(', ');
    try { navigator.clipboard?.writeText?.(text); } catch {}
  }

  _importManualHex() {
    const raw = (typeof prompt === 'function')
      ? prompt('Enter 3 hex colors, comma-separated (e.g. #ff0000, #00ff00, #0000ff)')
      : null;
    if (!raw) return;
    const parts = raw.split(/[,\s]+/).filter(Boolean).slice(0, 3);
    if (parts.length < 3) return;
    const stops = parts.map((p) => hexToRgb01(p));
    this._syncManualFromStops(stops);
    this._setCustomPaletteAndRefresh(stops);
  }

  _loadImagePalette(file, statusEl) {
    if (!file) return;
    if (statusEl) statusEl.textContent = 'loading...';
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        try {
          const W = 64;
          const H = 64;
          const canvas = document.createElement('canvas');
          canvas.width = W; canvas.height = H;
          const ctx = canvas.getContext('2d');
          if (!ctx) { if (statusEl) statusEl.textContent = 'canvas not supported'; return; }
          ctx.drawImage(img, 0, 0, W, H);
          const data = ctx.getImageData(0, 0, W, H).data;
          const stops = kmeans3(data, { iters: 30 });
          this._setCustomPaletteAndRefresh(stops);
          this._syncManualFromStops(stops);
          if (statusEl) statusEl.textContent = 'extracted 3 colors';
        } catch (err) {
          if (statusEl) statusEl.textContent = 'extract failed';
        }
      };
      img.onerror = () => { if (statusEl) statusEl.textContent = 'image load failed'; };
      img.src = String(ev.target?.result || '');
    };
    reader.onerror = () => { if (statusEl) statusEl.textContent = 'file read failed'; };
    reader.readAsDataURL(file);
  }

  _onKey(e) {
    if (e.key === 'p' || e.key === 'P') {
      setTimeout(() => this._refreshPalette(), 0);
    }
  }
}
